import React, { createContext, useState, useEffect, useMemo } from 'react';
import { vitalTestService } from '../services/vitalTestService';
import { toast } from 'react-toastify';

export const VitalTestContext = createContext();

export const VitalTestProvider = ({ children }) => {
  const [allVitalTests, setAllVitalTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVitalTest, setSelectedVitalTest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('edit'); // 'edit' or 'create'
  const [filters, setFilters] = useState({
    search: '',
    testType: '',
    hasFasting: '',
  });
  
  // Load all vital tests once
  const loadVitalTests = async () => {
    setLoading(true);
    try {
      const response = await vitalTestService.getVitalTests();
      if (response.data && response.data.success && response.data.data) {
        setAllVitalTests(response.data.data);
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error('Error loading vital tests:', error);
      toast.error('Failed to load vital tests');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Load vital tests on component mount (only if user is authenticated)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadVitalTests();
    }
  }, []);

  // Update filters without making API calls
  const updateFilters = (newFilters) => {
    setFilters(prevFilters => ({ ...prevFilters, ...newFilters }));
  };

  // Create a new vital test
  const createVitalTest = async (vitalTestData) => {
    setLoading(true);
    try {
      const response = await vitalTestService.createVitalTest(vitalTestData);
      if (response.data && response.data.success) {
        toast.success('Vital test created successfully');
        await loadVitalTests(); // Reload the list
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error creating vital test:', error);
      toast.error('Failed to create vital test');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update an existing vital test
  const updateVitalTest = async (vitalTestId, vitalTestData) => {
    setLoading(true);
    try {
      const response = await vitalTestService.updateVitalTest(vitalTestId, vitalTestData);
      if (response.data && response.data.success) {
        toast.success('Vital test updated successfully');
        await loadVitalTests(); // Reload the list
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error updating vital test:', error);
      toast.error('Failed to update vital test');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete a vital test
  const deleteVitalTest = async (vitalTestId) => {
    setLoading(true);
    try {
      const response = await vitalTestService.deleteVitalTest(vitalTestId);
      if (response.data && response.data.success) {
        toast.success('Vital test deleted successfully');
        await loadVitalTests(); // Reload the list
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error deleting vital test:', error);
      toast.error('Failed to delete vital test');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Open modal for editing
  const openEditModal = (vitalTest) => {
    setSelectedVitalTest(vitalTest);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  // Open modal for creating
  const openCreateModal = () => {
    setSelectedVitalTest(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVitalTest(null);
  };

  // Filter and sort vital tests locally based on current filters
  const filteredVitalTests = useMemo(() => {
    // First filter the vital tests
    const filtered = allVitalTests.filter(test => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matchesSearch = 
          (test.name && test.name.toLowerCase().includes(searchTerm)) || 
          (test.test_code && test.test_code.toLowerCase().includes(searchTerm)) ||
          (test.description && test.description.toLowerCase().includes(searchTerm));
        
        if (!matchesSearch) return false;
      }
      
      // Test Type filter
      if (filters.testType && filters.testType !== '') {
        if (!test.test_type || test.test_type !== filters.testType) return false;
      }
      
      // Has Fasting filter
      if (filters.hasFasting && filters.hasFasting !== '') {
        if (test.has_fasting !== filters.hasFasting) return false;
      }
      
      return true;
    });
    
    // Sort by name
    return filtered.sort((a, b) => {
      // Handle null/undefined values and convert to lowercase for case-insensitive comparison
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
  }, [allVitalTests, filters]);

  return (
    <VitalTestContext.Provider value={{ 
      vitalTests: filteredVitalTests,
      filteredVitalTests, // Explicitly expose this for pagination
      allVitalTests,
      selectedVitalTest,
      isModalOpen,
      modalMode,
      loading,
      filters,
      loadVitalTests,
      updateFilters,
      createVitalTest,
      updateVitalTest,
      deleteVitalTest,
      openEditModal,
      openCreateModal,
      closeModal
    }}>
      {children}
    </VitalTestContext.Provider>
  );
};
